import { apiConfig } from '@/config/apiConfig';
import type { Subdistrict, Village } from '@/services/locationService';
import type { HttpClient } from './documentApi';

/**
 * Shared client-agnostic implementation of the invite-mounted location
 * cascade: `GET /console/user_onboarding/invite/location/:district?/:subdistrict?`.
 *
 * Per `docs/onboarding/API/API-doc.md` §5, this endpoint accepts both
 * FusionAuth JWT and invite-token auth, so the same logic runs for the
 * backoffice (`apiClient`) and the candidate portal (`inviteApiClient`) —
 * the caller picks the client; this module just builds the URL and
 * normalises the response.
 */

export const getLocationDistricts = async (client: HttpClient): Promise<string[]> => {
  const url = apiConfig.endpoints.userOnboarding.inviteLocation;
  const { data } = await client.get<unknown>(url);
  return normalizeDistricts(data);
};

export const getLocationSubdistricts = async (
  client: HttpClient,
  district: string,
): Promise<Subdistrict[]> => {
  const url = `${apiConfig.endpoints.userOnboarding.inviteLocation}/${encodeURIComponent(district)}`;
  const { data } = await client.get<unknown>(url);
  return normalizeSubdistricts(data, district);
};

export const getLocationVillages = async (
  client: HttpClient,
  district: string,
  subdistrict: string,
): Promise<Village[]> => {
  const url = `${apiConfig.endpoints.userOnboarding.inviteLocation}/${encodeURIComponent(
    district,
  )}/${encodeURIComponent(subdistrict)}`;
  const { data } = await client.get<unknown>(url);
  return normalizeVillages(data, district, subdistrict);
};

/**
 * Reverse-lookup village + subdistrict + district names + pincode from a
 * stored LGD / master location id. Drives the LocationField when it's
 * prefilled with just a village id and needs to hydrate the cascade.
 *
 * Endpoint: `GET /console/user_onboarding/invite/location/village/:lgd_code`
 * (User-Onboarding.md §5.1).
 */
export interface VillageLookupResponse {
  location_name_in_english?: string;
  subdistrict_name_in_english?: string;
  district_name_in_english?: string;
  state_name_in_english?: string;
  pincode?: string;
}

export const getVillageByLgd = async (
  client: HttpClient,
  lgdCode: string | number,
): Promise<VillageLookupResponse | null> => {
  const url = `${apiConfig.endpoints.userOnboarding.inviteLocation}/village/${encodeURIComponent(
    String(lgdCode),
  )}`;
  try {
    const { data } = await client.get<{ data?: VillageLookupResponse } | VillageLookupResponse>(
      url,
    );
    if (data && typeof data === 'object') {
      const inner = (data as { data?: VillageLookupResponse }).data;
      if (inner && typeof inner === 'object') return inner;
      return data as VillageLookupResponse;
    }
    return null;
  } catch {
    return null;
  }
};

// ── normalizers ──────────────────────────────────────────────────────────
// The invite endpoint mirrors `locationController.getLocation` semantics:
// districts are strings, subdistricts are strings, villages are objects.
// These wrappers normalise into the shapes `LocationField` expects.

const normalizeDistricts = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((d) => {
      if (typeof d === 'string') return d;
      if (d && typeof d === 'object') {
        const o = d as Record<string, unknown>;
        return (o.name || o.district_name) as string | undefined;
      }
      return undefined;
    })
    .filter((n): n is string => typeof n === 'string' && n.length > 0);
};

const normalizeSubdistricts = (raw: unknown, district: string): Subdistrict[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    if (typeof item === 'string') {
      return {
        id: item.toLowerCase().replace(/\s+/g, '_'),
        subdistrict_name: item,
        district_name: district,
      };
    }
    if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>;
      return {
        id: (o.id as string) || (o.subdistrict_id as string) || '',
        subdistrict_name: ((o.subdistrict_name || o.name) as string) || '',
        district_name: (o.district_name as string) || district,
      };
    }
    return { id: '', subdistrict_name: '', district_name: district };
  });
};

const normalizeVillages = (raw: unknown, district: string, subdistrict: string): Village[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((item, index) => {
    if (typeof item === 'string') {
      return {
        village_id: `V_${index + 1001}`,
        village_name: item,
        subdistrict_name: subdistrict,
        district_name: district,
      };
    }
    if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>;
      return {
        village_id: ((o.id || o.village_id) as string) || `V_${index + 1001}`,
        village_name:
          ((o.location_name_in_english || o.village_name || o.name) as string) ||
          `Village ${index + 1}`,
        subdistrict_name: (o.subdistrict_name as string) || subdistrict,
        district_name: (o.district_name as string) || district,
      };
    }
    return {
      village_id: `V_${index + 1001}`,
      village_name: `Village ${index + 1}`,
      subdistrict_name: subdistrict,
      district_name: district,
    };
  });
};
